import { cn } from 'utils/classMerge'
import Label from 'components/ui/label'
import Text from 'components/ui/text'

import * as Primitive from '../../primitive'
import type { RadioOption } from '../../index'

interface CardRadioProps {
  option: RadioOption
}

const IconWrapper = ({ icon }: { icon: React.ReactNode }) => (
  <div
    data-testid="card-radio-icon"
    className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-text-zinc-500 group-hover:tw-text-zinc-900 tw-transition-colors"
  >
    {icon}
  </div>
)

const ContentSection = ({
  title,
  description,
  className,
}: {
  title?: string
  description?: string
  className?: string
}) => (
  <div
    className={cn(
      'tw-flex tw-flex-col tw-items-start tw-w-fit tw-text-left',
      className,
    )}
  >
    {title && (
      <Text
        as="h4"
        className={cn(
          'tw-my-0 tw-text-base tw-text-zinc-950 tw-text-left',
          description ? 'tw-font-semibold' : 'tw-font-medium',
        )}
      >
        {title}
      </Text>
    )}
    {description && (
      <Text
        as="small"
        className="tw-text-zinc-500 tw-my-0 tw-text-sm group-hover:tw-text-zinc-800 tw-transition-colors"
      >
        {description}
      </Text>
    )}
  </div>
)

const CardRadio = ({ option }: CardRadioProps) => {
  const uniqueValue = option.value?.toString() || option.id.toString()
  return (
    <>
      <Primitive.Item
        value={uniqueValue}
        id={uniqueValue}
        className="tw-peer tw-sr-only"
      />
      <Label
        htmlFor={uniqueValue}
        className={cn(
          'tw-group tw-shadow-sm tw-flex tw-flex-col tw-items-start tw-gap-6 tw-rounded-xl tw-border-solid tw-border tw-border-zinc-300 tw-bg-white tw-px-6 tw-py-4',
          'tw-cursor-pointer tw-transition-all',
          'peer-data-[state=checked]:tw-ring-2 peer-data-[state=checked]:tw-ring-orange-500 peer-data-[state=checked]:tw-border-transparent peer-data-[state=checked]:[&_svg]:tw-text-orange-500',
        )}
      >
        <div className="tw-flex tw-w-full tw-gap-4 tw-items-center tw-break-keep">
          {option.icon && <IconWrapper icon={option.icon} />}

          <ContentSection
            title={option.title}
            description={option.description}
          />

          {option.rightText && (
            <ContentSection
              title={option.rightText}
              description={option.rightTextHelper}
              className="tw-items-end"
            />
          )}
        </div>
      </Label>
    </>
  )
}

export { CardRadio }
